import { useEffect, useRef, useState } from 'react';
import { buildWhatsAppUrl, CONTACT } from '../../config/contact';

type Message = { from: 'bot' | 'user'; text: string; cta?: { label: string; href: string; target?: string } };

const QUICK_PROMPTS = [
  'How do I post a drone job?',
  'How are pilots verified?',
  'What does it cost?',
  'Talk to a human',
];

const respond = (prompt: string): Message[] => {
  const p = prompt.toLowerCase();
  if (p.includes('post') || p.includes('job') || p.includes('hire')) {
    return [
      { from: 'bot', text: 'Posting a job takes 60 seconds. Tell us what you need filmed, surveyed, or inspected; we route it to verified pilots in your city.' },
      { from: 'bot', text: 'Ready when you are 👇', cta: { label: 'Post a Job', href: '/post-job' } },
    ];
  }
  if (p.includes('verif') || p.includes('safe') || p.includes('trust') || p.includes('license') || p.includes('dgca')) {
    return [
      { from: 'bot', text: 'Every pilot signs our trust & safety policy. Verified pilots have DGCA-aware credentials, insurance documents, and reviewed portfolios. Payments stay in escrow until you approve deliverables.' },
      { from: 'bot', text: 'Browse only verified pilots:', cta: { label: 'Browse Pilots', href: '/pilots?license_verified=true' } },
    ];
  }
  if (p.includes('price') || p.includes('cost') || p.includes('fee') || p.includes('budget') || p.includes('quote')) {
    return [
      { from: 'bot', text: 'Pilots quote per job based on city, service, urgency, and crew size. Typical hourly rates range from ₹1,500 to ₹6,000+ depending on equipment and licensing.' },
      { from: 'bot', text: 'For a real-time price band on your project, post a job — we show a recommended budget instantly.', cta: { label: 'Get price hint', href: '/post-job' } },
    ];
  }
  if (p.includes('human') || p.includes('expert') || p.includes('call') || p.includes('whatsapp') || p.includes('talk')) {
    return [
      { from: 'bot', text: `Sure — connect with our team on WhatsApp at ${CONTACT.whatsappDisplay}.` },
      { from: 'bot', text: 'Tap below to open WhatsApp:', cta: { label: `WhatsApp ${CONTACT.whatsappDisplay}`, href: buildWhatsAppUrl('Hi AeroBott — I have a question, can someone help?'), target: '_blank' } },
    ];
  }
  if (p.includes('enterprise') || p.includes('multiple') || p.includes('multi') || p.includes('done')) {
    return [
      { from: 'bot', text: 'For enterprise programs (multi-site surveys, monitoring, audits) we run the program end-to-end.' },
      { from: 'bot', text: 'Talk to our enterprise team:', cta: { label: 'Done-for-You program', href: '/done-for-you' } },
    ];
  }
  return [
    { from: 'bot', text: "I can help with posting jobs, pilot verification, pricing, or enterprise programs. Pick a prompt below or type your question." },
    { from: 'bot', text: 'Need a human? We are on WhatsApp:', cta: { label: `WhatsApp ${CONTACT.whatsappDisplay}`, href: buildWhatsAppUrl('Hi AeroBott — I have a question, can someone help?'), target: '_blank' } },
  ];
};

const INITIAL: Message[] = [
  { from: 'bot', text: "Hi 👋 I'm AeroBot. How can I help?" },
];

export const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(INITIAL);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const send = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((prev) => [...prev, { from: 'user', text: trimmed }, ...respond(trimmed)]);
    setInput('');
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    send(input);
  };

  return (
    <>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open chat assistant"
          className="fixed bottom-5 left-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#000080] text-white shadow-lg ring-4 ring-[#000080]/20 hover:scale-105 transition"
        >
          <span className="text-2xl" aria-hidden>💬</span>
        </button>
      ) : null}
      {open ? (
        <div className="fixed bottom-5 left-5 z-40 flex h-[min(560px,calc(100vh-40px))] w-[min(360px,calc(100vw-40px))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <header className="flex items-center justify-between bg-[#000080] px-4 py-3 text-white">
            <div>
              <p className="text-sm font-black">AeroBot</p>
              <p className="text-[10px] text-white/70">Avg reply &lt; 1 min · backed by humans on WhatsApp</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" className="rounded p-1 text-white/80 hover:bg-white/10">✕</button>
          </header>
          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto bg-slate-50 p-3 text-sm">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${m.from === 'user' ? 'bg-[#000080] text-white' : 'bg-white text-slate-800 shadow-sm'}`}>
                  <p className="whitespace-pre-line text-sm">{m.text}</p>
                  {m.cta ? (
                    <a
                      href={m.cta.href}
                      target={m.cta.target}
                      rel={m.cta.target === '_blank' ? 'noopener noreferrer' : undefined}
                      className="mt-2 inline-block rounded-lg bg-[#FF9933] px-3 py-1.5 text-xs font-black text-[#000080]"
                    >
                      {m.cta.label} →
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
            {messages.length <= 1 ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {QUICK_PROMPTS.map((q) => (
                  <button key={q} type="button" onClick={() => send(q)} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700 shadow-sm hover:bg-slate-100">
                    {q}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <form onSubmit={onSubmit} className="border-t border-slate-200 bg-white p-2">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button type="submit" className="rounded-lg bg-[#000080] px-3 py-2 text-xs font-black text-white">Send</button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
};
